import React from "react";
import {
  Snackbar,
  IconButton,
  Typography,
  Box,
  Theme,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useIntl } from "react-intl";
import { styled } from "@mui/material/styles";
import { SyncJobDetails } from "store/sync-categories/SyncCategoriesTypes";

const StyledSnackbar = styled(Snackbar)({
  bottom: "10px",
  left: "10px",
  right: "auto",
  transform: "none",
  zIndex: 9,
});

const ContentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCompleted",
})<{ isCompleted?: boolean }>`
  background-color: ${({ theme }) => theme.palette.neutral.white};
  border: ${({ theme }) => `1px solid ${theme.palette.neutral.lightGrey}`};
  border-radius: 4px;
  display: inline-block;
  overflow: auto;
  transition: all 0.3s ease;
  min-width: ${(props) => (props.isCompleted ? "400px" : "200px")};
  min-height: ${(props) => (props.isCompleted ? "300px" : "60px")};
`;

const Header = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.neutral.lightGrey}`,
  height: "16px",
  backgroundColor: theme.palette.neutral.darkGreyishBlue,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px",
}));

const Title = styled("span")(({ theme }) => ({
  color: theme.palette.neutral.white,
  fontSize: "12px",
  marginLeft: 0,
}));

const CloseButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "isCompleted",
})<{ isCompleted?: boolean }>(({ isCompleted, theme }) => ({
  display: isCompleted ? "contents" : "none",
  "&:hover": {
    cursor: "pointer",
  },
  "& .MuiSvgIcon-root": {
    color: theme.palette.neutral.white,
  },
}));

const MainSection = styled(Box)({
  padding: "10px",
  display: "flex",
  flexDirection: "column",
});

const ProgressSection = styled(Box)({
  textAlign: "center",
});

const ProgressText = styled(Box)({
  fontSize: "12px",
  marginBottom: "10px",
  textAlign: "center",
});

const ProgressBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCompleted",
})<{ isCompleted: boolean }>(({ isCompleted, theme }) => ({
  height: "8px",
  margin: "0 auto",
  backgroundColor: theme.palette.neutral.whiteSmoke,
  boxShadow: `inset 0 1px 2px ${theme.palette.neutral.lightGrey}`,
  borderRadius: "4px",
  overflow: "hidden",
  width: isCompleted ? "88%" : "200px",
}));

const ProgressFill = styled(Box, {
  shouldForwardProp: (prop) => prop !== "width" && prop !== "backgroundColor",
})<{ width: number; backgroundColor: string }>(
  ({ width, backgroundColor }) => ({
    height: "8px",
    transition: "width 0.3s ease",
    width: `${width}%`,
    backgroundColor,
  }),
);

const DetailsSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isCompleted",
})<{ isCompleted: boolean }>(({ isCompleted }) => ({
  display: isCompleted ? "block" : "none",
  marginLeft: "30px",
  marginTop: "20px",
  width: "85%",
}));

const DetailLabel = styled(Typography)({
  textTransform: "uppercase",
});

const DetailValue = styled(Typography)({
  marginLeft: "30px",
  lineHeight: 1.5,
  fontSize: "14px",
});

const StatusText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "color",
})<{ color: string }>(({ color }) => ({
  fontWeight: "bold",
  textTransform: "uppercase",
  color,
  marginLeft: "30px",
}));

const TargetList = styled(Box)({
  listStyleType: "disc",
  marginTop: "5px",
});

const TargetItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isFailed",
})<{ isFailed: boolean }>(({ isFailed, theme }) => ({
  margin: "5px 0",
  lineHeight: 1.5,
  fontSize: "14px",
  color: isFailed ? theme.palette.neutral.red : "inherit",
}));

const TargetErrorBox = styled(Box)({
  marginTop: "3px",
});

const StyledBox = styled(Box)({
  marginBottom: "5px",
});

interface SyncJobProgressSnackbarProps {
  open: boolean;
  onClose: () => void;
  jobDetails: SyncJobDetails | null;
}

const SyncJobProgressSnackbar: React.FC<SyncJobProgressSnackbarProps> = ({
  open,
  onClose,
  jobDetails,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  if (!jobDetails || !jobDetails.syncJobId) {
    return null;
  }

  const calculateProgress = () => {
    const totalTargets = jobDetails.targets.length;
    const completedTargets = jobDetails.targets.filter(
      (target) => target.status === "COMPLETED",
    ).length;
    const failedTargets = jobDetails.targets.filter(
      (target) => target.status === "FAILED",
    ).length;

    return {
      completed: completedTargets,
      failed: failedTargets,
      total: totalTargets,
      percentage:
        totalTargets > 0
          ? ((completedTargets + failedTargets) / totalTargets) * 100
          : 0,
    };
  };

  const getStatusColor = (status: string, theme: Theme) => {
    switch (status) {
      case "TRIGGERED":
        return theme.palette.neutral.seaSerpent;
      case "IN_PROGRESS":
        return theme.palette.neutral.seaSerpent;
      case "FAILED":
        return theme.palette.neutral.red;
      case "COMPLETED":
        return theme.palette.neutral.green;
      default:
        return theme.palette.neutral.seaSerpent;
    }
  };

  const progress = calculateProgress();
  const isCompleted =
    jobDetails.status === "COMPLETED" || jobDetails.status === "FAILED";

  const source = `${jobDetails.source.storeId} / ${jobDetails.source.catalogId} / ${jobDetails.source.categoryId}`;

  return (
    <StyledSnackbar
      open={open}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <ContentContainer isCompleted={isCompleted}>
        <Header>
          <Title>
            {!isCompleted && (
              <Typography variant="syncBarHeader">
                {intl.formatMessage({
                  id: "syncJob.inProgress",
                  defaultMessage: "Synchronizing...",
                })}
              </Typography>
            )}
          </Title>
          <CloseButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            isCompleted={isCompleted}
          >
            <CloseIcon fontSize="small" />
          </CloseButton>
        </Header>

        <MainSection>
          <ProgressSection>
            <ProgressText>
              {progress.completed + progress.failed} / {progress.total}
            </ProgressText>
            <ProgressBar isCompleted={isCompleted}>
              <ProgressFill
                width={progress.percentage}
                backgroundColor={getStatusColor(jobDetails.status, theme)}
              />
            </ProgressBar>
          </ProgressSection>

          <DetailsSection isCompleted={isCompleted}>
            <StyledBox>
              <DetailLabel variant="title">
                {intl.formatMessage({
                  id: "syncJob.status",
                  defaultMessage: "STATUS",
                })}
              </DetailLabel>
              <StatusText
                variant="subtitle2"
                color={getStatusColor(jobDetails.status, theme)}
              >
                {jobDetails.status}
              </StatusText>
            </StyledBox>
            {jobDetails.status === "FAILED" && (
              <StyledBox>
                <DetailLabel variant="title">
                  {intl.formatMessage({
                    id: "syncJob.error",
                    defaultMessage: "ERROR",
                  })}
                </DetailLabel>
                <StatusText
                  variant="subtitle2"
                  color={getStatusColor(jobDetails.status, theme)}
                >
                  {jobDetails.error.message ??
                    jobDetails.error.Cause ??
                    jobDetails.error.Error}
                </StatusText>
              </StyledBox>
            )}
            <StyledBox>
              <DetailLabel variant="title">
                {intl.formatMessage({
                  id: "syncJob.jobId",
                  defaultMessage: "SYNC JOB ID",
                })}
              </DetailLabel>
              <DetailValue variant="subtitle2">
                {jobDetails.syncJobId}
              </DetailValue>
            </StyledBox>
            <StyledBox>
              <DetailLabel variant="title">
                {intl.formatMessage({
                  id: "syncJob.source",
                  defaultMessage: "SOURCE CATEGORY",
                })}
              </DetailLabel>
              <DetailValue variant="subtitle2">{source}</DetailValue>
            </StyledBox>

            <StyledBox>
              <DetailLabel variant="title">
                {intl.formatMessage({
                  id: "syncJob.targets",
                  defaultMessage: "TARGET CATEGORIES",
                })}
              </DetailLabel>
              <TargetList component="ul">
                {jobDetails.targets.map((target, index) => (
                  <TargetItem
                    key={index}
                    component="li"
                    isFailed={target.status === "FAILED"}
                  >
                    <Box>
                      <Typography variant="subtitle2">
                        {target.storeId} / {target.catalogId} /{" "}
                        {target.categoryId}
                      </Typography>
                      <Box>
                        <Typography variant="title">
                          {intl.formatMessage({
                            id: "syncJob.status",
                            defaultMessage: "STATUS",
                          })}
                        </Typography>{" "}
                        <StatusText
                          variant="subtitle2"
                          color={getStatusColor(target.status, theme)}
                        >
                          {target.status}
                        </StatusText>
                        {target.status === "FAILED" &&
                          target.error &&
                          target.error.message && (
                            <TargetErrorBox>
                              <Typography variant="title">
                                {intl.formatMessage({
                                  id: "syncJob.error",
                                  defaultMessage: "ERROR",
                                })}
                              </Typography>{" "}
                              <StatusText
                                variant="subtitle2"
                                color={getStatusColor(jobDetails.status, theme)}
                              >
                                {target.error.message}
                              </StatusText>
                            </TargetErrorBox>
                          )}
                      </Box>
                    </Box>
                  </TargetItem>
                ))}
              </TargetList>
            </StyledBox>
          </DetailsSection>
        </MainSection>
      </ContentContainer>
    </StyledSnackbar>
  );
};

export default SyncJobProgressSnackbar;
